import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentProps, createContext } from 'react';

import { cn } from '@/shared/utils/cn';
import type { Rarity } from '@/shared/types/rarity';
import { rarityGlowShadows, rarityOuterClasses } from '@/shared/utils/rarity';
import { PremiumCardBorder } from './premiumCardBorder';

const RarityCardContext = createContext<Rarity | null>(null);

const outerVariants = cva('rounded-[28px] relative bg-black border-2 mb-1', {
  variants: {
    rarity: {
      COMMON: rarityOuterClasses.COMMON,
      UNCOMMON: rarityOuterClasses.UNCOMMON,
      RARE: rarityOuterClasses.RARE,
      EPIC: rarityOuterClasses.EPIC,
      LEGENDARY: rarityOuterClasses.LEGENDARY,
    },
    size: {
      small: 'p-1',
      large: 'p-2',
    },
  },
  compoundVariants: [
    { rarity: 'EPIC', size: 'large', className: rarityGlowShadows.EPIC },
    {
      rarity: 'LEGENDARY',
      size: 'large',
      className: rarityGlowShadows.LEGENDARY,
    },
  ],
  defaultVariants: {
    rarity: 'UNCOMMON',
    size: 'large',
  },
});

export interface RarityCardOuterProps
  extends ComponentProps<'div'>, VariantProps<typeof outerVariants> {
  rarity: Rarity;
  isPremium?: boolean;
  corners?: 'top' | 'bottom' | 'both';
}

function RarityCardOuter({
  className,
  rarity,
  size = 'large',
  isPremium = false,
  corners = 'both',
  children,
  ...props
}: RarityCardOuterProps) {
  return (
    <RarityCardContext value={rarity}>
      <div
        data-slot="rarity-card-outer"
        className={cn(outerVariants({ rarity, size }), className)}
        {...props}
      >
        {isPremium && <PremiumCardBorder rarity={rarity} corners={corners} />}
        {children}
      </div>
    </RarityCardContext>
  );
}

export interface RarityCardInnerProps extends ComponentProps<'div'> {}

function RarityCardInner({
  className,
  children,
  ...props
}: RarityCardInnerProps) {
  return (
    <div
      data-slot="rarity-card-inner"
      className={cn('bg-titan-950 rounded-[20px]', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export const RarityCard = {
  Outer: RarityCardOuter,
  Inner: RarityCardInner,
};
